import { action, computed, makeAutoObservable } from 'mobx';
import { CalculationApi, CalculationApiResponse, CalculationStatus } from '../../api/CalculationApi';
import { Calculation } from '../models/Calculation';
import { Transaction } from '../models/Transaction';
import { EntitiesStore } from './EntitiesStore';

export class CalculationStore {
  calculations: Map<number, Calculation> = new Map();
  groupCalculations: Map<number, Set<number>> = new Map();
  isLoading: boolean = false;
  entitiesStore: EntitiesStore = null;
  loadedGroups: number[] = [];

  constructor(entitiesStore: EntitiesStore) {
    makeAutoObservable(this);
    this.entitiesStore = entitiesStore;
  }

  getCalculation = (id: number): Calculation => {
    return computed(() => this.calculations.get(id)).get();
  };

  load = async (groupId: number, id: number, force: boolean = false): Promise<Calculation> => {
    if (this.calculations.has(id) && !this.calculations.get(id).isPartialLoad && !force) {
      return Promise.resolve(this.calculations.get(id));
    }

    this.isLoading = true;
    const apiCalculation = await CalculationApi.getCalculation(groupId, id);

    const calculation = await this.parseCalculation(groupId, apiCalculation);

    if (calculation.buildStatus === CalculationStatus.complete) {
      this.calculations.set(calculation.id, calculation);

      if (calculation.locked) {
        calculation.loadLatestId();
      }
    }

    this.isLoading = false;
    return calculation;
  };

  recalculate = async (groupId: number, id: number): Promise<Calculation> => {
    const apiCalculation = await CalculationApi.recalculate(groupId, id);
    const calculation = await this.parseCalculation(groupId, apiCalculation);

    this.addCalculation(groupId, calculation);

    return calculation;
  };

  delete = (groupId: number, calculationId: number): Promise<void> => {
    return CalculationApi.deleteCalculation(groupId, calculationId)
      .then(action(() => {
        this.calculations.delete(calculationId);
        this.groupCalculations.get(groupId).delete(calculationId);
      }));
  };

  loadGroup = async (groupId: number, force: boolean = false): Promise<void> => {
    if (this.loadedGroups.includes(groupId) && !force) {
      return Promise.resolve();
    }

    const calculations = await CalculationApi.getGroup(groupId);

    // Do this the old fashioned way so we can wait properly
    for (let i = 0; i < calculations.length; i++) {
      const calculation = await this.parseCalculation(groupId, calculations[i]);
      this.addCalculation(groupId, calculation);
    }

    this.loadedGroups.push(groupId);
  };

  addCalculation = (groupId: number, calculation: Calculation) => {
    this.calculations.set(calculation.id, calculation);
    this.addGroupCalculation(groupId, calculation.id);
  };

  addGroupCalculation = (groupId: number, calculationId: number): void => {
    if (!this.groupCalculations.has(groupId)) {
      this.groupCalculations.set(groupId, new Set<number>());
    }
    this.groupCalculations.get(groupId).add(calculationId);
  };

  calculationsForGroup = (groupId: number): Calculation[] => {
    return computed(() =>{
      if (!this.groupCalculations.has(groupId)) {
        return [];
      }

      return [...this.groupCalculations.get(groupId)]
        .map(calculationId => this.calculations.get(calculationId))
        .sort((a, b) => a.end > b.end ? -1 : 1);
    }).get();
  };

  parseCalculation = async (groupId: number, apiCalculation: CalculationApiResponse): Promise<Calculation> => {
    const calculation = Calculation.fromApi(apiCalculation);
    calculation.groupId = groupId;

    if (calculation.buildStatus !== CalculationStatus.complete) {
      return calculation;
    }

    calculation.entityTransactions = new Map();

    // Split the transactions up by organisation
    // Do this the old-fashioned way so we can fetch rules properly
    if (apiCalculation.matched) {
      for (let i = 0; i < apiCalculation.matched.length; i++) {
        const transactionMatch = apiCalculation.matched[i];
        const entityId = transactionMatch.organisationId;
        if (!calculation.entityTransactions.has(entityId)) {
          calculation.entityTransactions.set(entityId, []);
        }

        if (!calculation.entityRechargeTotals.has(entityId)) {
          calculation.entityRechargeTotals.set(entityId, 0);
        }

        const transaction = Transaction.fromMatchApi(transactionMatch);

        if (transaction.isNew(calculation.lockDate)) {
          calculation.hasNewTransactions = true;
        }

        if (!transaction.isUnmatched()) {
          let rule = this.entitiesStore.getRuleByRuleApportionmentId(transaction.assignedRule);

          if (rule === null) {
            rule = await this.entitiesStore.loadRuleApportionmentId(groupId, transaction.assignedRule);
          }

          transaction.rechargeValues = transaction.rechargeValues
            .map(recharge => {
              calculation.entityRechargeTotals.set(entityId, calculation.entityRechargeTotals.get(entityId) + recharge.value);

              return recharge;
            });
        }

        calculation.entityTransactions.get(entityId).push(transaction);
      }
      calculation.isPartialLoad = false;
    } else {
      calculation.isPartialLoad = true;
    }

    return calculation;
  };

  unlock = (calculation: Calculation): Promise<Calculation> => {
    const oldId = calculation.id;
    return CalculationApi.unlock(calculation.groupId, calculation.id)
      .then(async apiCalculation => {
        // Just parse the calculation summary, so the calculation is re-calculated when we land on the page
        const newCalculation = Calculation.fromApi(apiCalculation);
        newCalculation.groupId = calculation.groupId;
        newCalculation.isPartialLoad = true;

        if (calculation.buildStatus === CalculationStatus.complete) {
          this.addCalculation(newCalculation.groupId, newCalculation);
        }

        this.calculations.delete(oldId);
        this.groupCalculations.get(newCalculation.groupId).delete(oldId);

        return newCalculation;
      });
  };

  invalidateCalculationsCache = (groupId: number) => {
    this.groupCalculations.get(groupId)
      ?.forEach(calcId => this.calculations.get(calcId).isPartialLoad = true);
  };
}
